/**
 * Workflow-Centric Case Dashboard
 * 
 * This component is built around the WorkflowStateManager as the central metronome.
 * It provides a clean, workflow-driven interface for both supplier and customer cases.
 * 
 * Architecture:
 * - WorkflowStateManager drives navigation and state transitions
 * - Case-type specific tabs (supplier vs customer)
 * - Clear separation of workflow stages
 * - Integrated supplier workflow tracking within customer cases
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  Grid,
  Paper,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  RequestQuote as QuoteIcon,
  Email as EmailIcon,
  Timeline as TimelineIcon,
  Visibility as VisibilityIcon,
  Compare as CompareIcon,
  ShoppingCart as ConvertIcon,
  AttachFile as AttachFileIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as OrderIcon
} from '@mui/icons-material';

import { CaseData } from '../../services/caseManagementService';
import { WorkflowStateManager } from './shared/WorkflowStateManager';
import { useNotification } from '../../contexts/NotificationContext';
import CustomerPositionEditor from './customer/CustomerPositionEditor';
import QuoteAggregationManager from './integration/QuoteAggregationManager';
import { SupplierQuoteAggregator } from './integration/SupplierQuoteAggregator';
import CustomerCaseFileManager from './customer/CustomerCaseFileManager';
import EmailIntegrationPanel from './shared/EmailIntegrationPanel';
import { QuoteRequestViewer } from './supplier/QuoteRequestViewer';
import CustomerPositionOverview from './customer/CustomerPositionOverview';
import { QuoteRequestSummary } from './supplier/QuoteRequestSummary';
import QuoteManagementHub from './supplier/QuoteManagementHub';
import CustomerPreprocessingManager from './customer/CustomerPreprocessingManager';
import { CustomerApprovalManager } from './customer/CustomerApprovalManager';

// Helper functions for status display
function getDisplayStatus(status: string, workflowState: string): string {
  // Map workflow states to display labels
  switch (workflowState) {
    case 'completed':
    case 'case_closed':
      return 'Abgeschlossen';
    case 'case_lost':
      return 'Verloren';
    case 'waiting_for_file_upload':
      return 'Warten auf Upload';
    case 'case_won':
      return 'Gewonnen';
    case 'in_progress':
    case 'preprocessing_needed':
    case 'waiting_for_customer_decision':
      return 'In Bearbeitung';
    case 'converted_to_order':
      return 'Auftragserteilung';
    case 'quote_selected':
      return 'Angebot ausgewählt';
    case 'supplier_response_received':
      return 'Antwort erhalten';
    case 'new':
    case 'case_created':
      return 'Neu';
    default:
      return status || workflowState || 'Unbekannt';
  }
}

function getStatusColor(status: string, workflowState: string): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' {
  switch (workflowState) {
    case 'completed':
    case 'case_closed':
    case 'converted_to_order':
      return 'success';
    case 'case_lost':
      return 'error';
    case 'waiting_for_file_upload':
      return 'warning';
    case 'case_won':
    case 'quote_selected':
      return 'primary';
    case 'in_progress':
    case 'preprocessing_needed':
    case 'waiting_for_customer_decision':
    case 'supplier_response_received':
      return 'secondary';
    case 'new':
    case 'case_created':
      return 'default';
    default:
      return 'default';
  }
}

interface WorkflowCaseDashboardProps {
  open: boolean;
  onClose: () => void;
  selectedCase: CaseData | null;
  onCaseUpdated?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workflow-tabpanel-${index}`}
      aria-labelledby={`workflow-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const WorkflowCaseDashboard: React.FC<WorkflowCaseDashboardProps> = ({
  open,
  onClose,
  selectedCase,
  onCaseUpdated
}) => {
  const { showNotification } = useNotification();
  
  // State management
  const [selectedTab, setSelectedTab] = useState(0);
  const [currentWorkflowState, setCurrentWorkflowState] = useState<string>('case_created');
  const [loading, setLoading] = useState(false);

  // Reset tab when case changes
  useEffect(() => {
    setSelectedTab(0);
    if (selectedCase) {
      // Load initial workflow state
      loadWorkflowState();
    }
  }, [selectedCase]);

  const loadWorkflowState = async () => {
    if (!selectedCase) return;
    
    try {
      const response = await fetch(`/api/v3/workflow/cases/${selectedCase.id}/state`);
      const data = await response.json();
      
      if (data.success) {
        setCurrentWorkflowState(data.data.workflow_state);
      }
    } catch (error) {
      console.error('Error loading workflow state:', error);
    }
  };

  // Handle workflow state transitions with proper persistence
  const handleWorkflowStateTransition = async (fromState: string, toState: string) => {
    if (!selectedCase) return;

    try {
      setLoading(true);
      console.log(`🔄 Transitioning workflow: ${fromState} → ${toState} for case ${selectedCase.id}`);
      
      // Persist to backend via API
      const response = await fetch(`/api/v3/workflow/cases/${selectedCase.id}/state`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          workflow_state: toState,
          metadata: {
            previous_state: fromState,
            timestamp: new Date().toISOString(),
            user: 'Current User', // TODO: Get from auth context
            transition_source: 'workflow_case_dashboard'
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update workflow state');
      }

      const result = await response.json();
      console.log('✅ Workflow state updated successfully:', result);
      
      // Update local state
      setCurrentWorkflowState(toState);
      
      // Show notification
      showNotification(`Workflow updated to: ${toState}`, 'success');
      
      // Update case metadata
      onCaseUpdated?.();
      
    } catch (error: any) {
      console.error('❌ Error updating workflow state:', error);
      showNotification('Error updating workflow state: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle navigation actions from WorkflowStateManager
  const handleWorkflowComponentAction = (action: string, data?: any) => {
    switch (action) {
      case 'navigate_to_tab':
        if (data?.tabLabel) {
          navigateToTabByLabel(data.tabLabel);
          showNotification(`Navigated to: ${data.tabLabel}`, 'info');
        }
        break;
      default:
        console.log(`Unknown workflow component action: ${action}`, data);
        break;
    }
  };

  // Navigate to tab by label
  const navigateToTabByLabel = (label: string) => {
    const tabs = getAvailableTabs();
    const tabIndex = tabs.findIndex(tab => tab.label === label);
    if (tabIndex !== -1) {
      setSelectedTab(tabIndex);
    }
  };

  // Get available tabs based on case type and workflow state
  const getAvailableTabs = () => {
    if (!selectedCase) return [];

    const baseTabs = [
      { label: 'Workflow Overview', icon: <TimelineIcon /> }
    ];

    if (selectedCase.case_type === 'supplier_case') {
      // Supplier case tabs
      baseTabs.push(
        { label: 'Angebot Verwalten', icon: <QuoteIcon /> },
        { label: 'Receive & Select Quotes', icon: <CompareIcon /> },
        { label: 'Order Integration', icon: <ConvertIcon /> },
        { label: 'E-Mail', icon: <EmailIcon /> }
      );
    } else if (selectedCase.case_type === 'customer_order') {
      // Customer case tabs
      baseTabs.push(
        { label: 'Preprocessing', icon: <EditIcon /> },
        { label: 'Waiting Customer Approval', icon: <CheckCircleIcon /> },
        { label: 'Files', icon: <AttachFileIcon /> },
        { label: 'E-Mail', icon: <EmailIcon /> }
      );
    }

    return baseTabs;
  };

  const availableTabs = getAvailableTabs();

  if (!selectedCase) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '95vh', maxHeight: '95vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            {selectedCase.case_type === 'supplier_case' ? <BusinessIcon /> : <PersonIcon />}
            <Box>
              <Typography variant="h6">
                {selectedCase.case_number || selectedCase.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCase.case_type === 'supplier_case' ? 'Supplier Case' : 'Customer Order'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Workflow State Manager - The Metronome */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <WorkflowStateManager
            currentState={currentWorkflowState}
            onStateTransition={handleWorkflowStateTransition}
            caseId={selectedCase.id}
            caseType={selectedCase.case_type === 'supplier_case' ? 'supplier_case' : 'customer_order'}
            readonly={false}
            onComponentAction={handleWorkflowComponentAction}
            compactMode={false}
            showNextActions={true}
          />
        </Box>

        {/* Tab Navigation */}
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {availableTabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              id={`workflow-tab-${index}`}
              aria-controls={`workflow-tabpanel-${index}`}
            />
          ))}
        </Tabs>

        {/* Tab Content */}
        {loading && <LinearProgress />}

        {/* Workflow Overview Tab */}
        <TabPanel value={selectedTab} index={0}>
          <WorkflowOverviewTab 
            selectedCase={selectedCase}
            currentWorkflowState={currentWorkflowState}
            onWorkflowTransition={handleWorkflowStateTransition}
          />
        </TabPanel>

        {/* Case-specific tabs */}
        {selectedCase.case_type === 'supplier_case' && (
          <>
            <TabPanel value={selectedTab} index={1}>
              <QuoteRequestViewer 
                caseId={selectedCase.id}
                caseNumber={selectedCase.case_number}
                caseData={selectedCase}
                currentWorkflowState={currentWorkflowState}
                onRefresh={() => {
                  // Trigger a refresh to reload workflow state and parent data
                  console.log('QuoteRequestViewer refresh triggered');
                  loadWorkflowState();
                }}
                onEmailSent={() => {
                  console.log('Email sent from QuoteRequestViewer');
                  // Refresh workflow state after email is sent
                  loadWorkflowState();
                }}
              />
            </TabPanel>
            <TabPanel value={selectedTab} index={2}>
              <QuoteManagementHub
                caseId={selectedCase.id}
                caseNumber={selectedCase.case_number || selectedCase.id}
                currentWorkflowState={currentWorkflowState}
                onQuoteSelected={(quote) => {
                  console.log('Quote selected:', quote);
                }}
                onOrderConverted={(orderId) => {
                  console.log('Order converted:', orderId);
                }}
                onWorkflowTransition={(newState) => {
                  console.log('Workflow transition to:', newState);
                  handleWorkflowStateTransition(currentWorkflowState, newState);
                }}
              />
            </TabPanel>
            <TabPanel value={selectedTab} index={3}>
              <OrderIntegrationTab selectedCase={selectedCase} />
            </TabPanel>
            <TabPanel value={selectedTab} index={4}>
              <EmailTab selectedCase={selectedCase} />
            </TabPanel>
          </>
        )}

        {selectedCase.case_type === 'customer_order' && (
          <>
            <TabPanel value={selectedTab} index={1}>
              <CustomerPreprocessingTab 
                selectedCase={selectedCase}
                currentWorkflowState={currentWorkflowState}
                onWorkflowTransition={handleWorkflowStateTransition}
              />
            </TabPanel>
            <TabPanel value={selectedTab} index={2}>
              <CustomerApprovalTab 
                selectedCase={selectedCase}
                currentWorkflowState={currentWorkflowState}
                onWorkflowTransition={handleWorkflowStateTransition}
              />
            </TabPanel>
            <TabPanel value={selectedTab} index={3}>
              <FilesTab selectedCase={selectedCase} />
            </TabPanel>
            <TabPanel value={selectedTab} index={4}>
              <EmailTab selectedCase={selectedCase} />
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Workflow Overview Tab Component
const WorkflowOverviewTab: React.FC<{
  selectedCase: CaseData;
  currentWorkflowState: string;
  onWorkflowTransition: (from: string, to: string) => Promise<void>;
}> = ({ selectedCase, currentWorkflowState, onWorkflowTransition }) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Case Information
            </Typography>
            <Box display="flex" flex-direction="column" gap={1}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Case Number:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedCase.case_number || selectedCase.id}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Case Type:</Typography>
                <Chip 
                  label={selectedCase.case_type} 
                  size="small"
                  color={selectedCase.case_type === 'supplier_case' ? 'primary' : 'secondary'}
                />
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Status:</Typography>
                <Chip 
                  label={getDisplayStatus(selectedCase.status, currentWorkflowState)} 
                  size="small" 
                  color={getStatusColor(selectedCase.status, currentWorkflowState)}
                />
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Created:</Typography>
                <Typography variant="body2">
                  {new Date(selectedCase.created_at).toLocaleDateString('de-DE')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Workflow State
            </Typography>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>State:</strong> {currentWorkflowState}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Use the workflow controls above to transition to the next stage.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quote Request Overview
            </Typography>
            {selectedCase.case_type === 'supplier_case' ? (
              <QuoteRequestSummary 
                caseId={selectedCase.id}
                caseNumber={selectedCase.case_number}
                caseData={selectedCase}
                onEmailAction={() => {
                  console.log('Email action from workflow overview');
                }}
                onViewDetails={() => {
                  console.log('View details clicked - switch to Angebot Verwalten tab');
                }}
              />
            ) : (
              <CustomerPositionOverview 
                caseId={selectedCase.id}
                caseNumber={selectedCase.case_number}
                customerCaseData={selectedCase}
                onEditPositions={() => {
                  console.log('Edit positions clicked - switch to Customer Preprocessing tab');
                  // TODO: Implement tab switching to Customer Preprocessing
                }}
                onManageSupplierQuotes={() => {
                  console.log('Manage supplier quotes clicked - switch to Supplier Quote Management tab');
                  // TODO: Implement tab switching to Supplier Quote Management
                }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Placeholder tab components - to be implemented
const SupplierQuoteManagementTab: React.FC<{ selectedCase: CaseData }> = ({ selectedCase }) => (
  <QuoteRequestViewer 
    caseId={selectedCase.id}
    caseNumber={selectedCase.case_number}
    caseData={selectedCase}
    onRefresh={() => {
      // Optionally trigger a refresh of the parent component
      console.log('QuoteRequestViewer refresh triggered');
    }}
    onEmailSent={() => {
      console.log('Email sent from QuoteRequestViewer');
    }}
  />
);

const OrderIntegrationTab: React.FC<{ selectedCase: CaseData }> = ({ selectedCase }) => {
  const [relatedOrders, setRelatedOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    loadRelatedOrders();
  }, [selectedCase]);

  const loadRelatedOrders = async () => {
    if (!selectedCase) return;
    
    console.log(`🔍 [ORDER-INTEGRATION] Loading orders for case: ${selectedCase.id}`);
    setLoading(true);
    try {
      // Skip the primary endpoint that doesn't exist and go directly to the working endpoint
      console.log(`� [ORDER-INTEGRATION] Trying: /api/v3/orders?case_id=${selectedCase.id}`);
      const ordersResponse = await fetch(`/api/v3/orders?case_id=${selectedCase.id}`);
      
      console.log(`📊 [ORDER-INTEGRATION] Response status: ${ordersResponse.status}`);
      console.log(`📊 [ORDER-INTEGRATION] Response headers:`, Object.fromEntries(ordersResponse.headers.entries()));
      
      if (!ordersResponse.ok) {
        console.error(`❌ [ORDER-INTEGRATION] HTTP error! status: ${ordersResponse.status}`);
        setRelatedOrders([]);
        return;
      }

      const ordersData = await ordersResponse.json();
      
      console.log(`� [ORDER-INTEGRATION] API response:`, ordersData);
      console.log(`📊 [ORDER-INTEGRATION] Response type:`, typeof ordersData);
      console.log(`📊 [ORDER-INTEGRATION] Success field:`, ordersData.success);
      console.log(`📊 [ORDER-INTEGRATION] Data field:`, ordersData.data);
      
      if (ordersData.success && ordersData.data && Array.isArray(ordersData.data)) {
        console.log(`✅ [ORDER-INTEGRATION] Found ${ordersData.data.length} orders`);
        setRelatedOrders(ordersData.data);
      } else if (ordersData.data && Array.isArray(ordersData.data)) {
        // Handle case where success field might be missing but data exists
        console.log(`✅ [ORDER-INTEGRATION] Found ${ordersData.data.length} orders (no success field)`);
        setRelatedOrders(ordersData.data);
      } else {
        console.log(`❌ [ORDER-INTEGRATION] No valid orders found in response`);
        setRelatedOrders([]);
      }
    } catch (error) {
      console.error('🚨 [ORDER-INTEGRATION] Error loading related orders:', error);
      showNotification('Error loading related orders', 'error');
      setRelatedOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderAction = async (orderId: string, action: string) => {
    try {
      switch (action) {
        case 'view_details':
          // Open order detail modal or navigate to order view
          console.log('Opening order details for:', orderId);
          break;
        case 'track_delivery':
          // Show delivery tracking
          console.log('Tracking delivery for order:', orderId);
          break;
        case 'mark_completed':
          // Mark order as completed
          const response = await fetch(`/api/v3/orders/${orderId}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'completed' })
          });
          
          if (response.ok) {
            showNotification('Order marked as completed', 'success');
            loadRelatedOrders(); // Reload to show updated status
          }
          break;
      }
    } catch (error) {
      console.error('Error performing order action:', error);
      showNotification('Error performing action', 'error');
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                <OrderIcon sx={{ mr: 1 }} />
                Order Integration & Tracking
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Track orders generated from this supplier quote case and monitor their progress 
                through the fulfillment workflow.
              </Typography>

              {loading ? (
                <LinearProgress />
              ) : relatedOrders.length === 0 ? (
                <Alert severity="info">
                  <Typography variant="body2">
                    No orders have been generated from this case yet.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Complete the quote selection and conversion process to generate orders.
                  </Typography>
                </Alert>
              ) : (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Related Orders ({relatedOrders.length})
                  </Typography>
                  
                  {relatedOrders.map((order, index) => (
                    <Card key={order.id} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {order.order_number || order.id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {order.order_type?.toUpperCase()} Order
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Created: {new Date(order.created_at).toLocaleDateString('de-DE')}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            {/* Import StatusProgress component for order status visualization */}
                            <Typography variant="body2" gutterBottom>
                              Order Status Progress:
                            </Typography>
                            <Chip 
                              label={order.status?.toUpperCase() || 'UNKNOWN'} 
                              color={getOrderStatusColor(order.status)}
                              size="small"
                              sx={{ mb: 1 }}
                            />
                            {/* TODO: Add StatusProgress component here */}
                            <LinearProgress 
                              variant="determinate" 
                              value={getOrderProgressPercentage(order.status)} 
                              sx={{ mt: 1 }}
                            />
                          </Grid>
                          
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Total Value: {order.total_price ? `€${order.total_price}` : 'TBD'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Positions: {order.positions?.length || 0}
                            </Typography>
                            {order.due_date && (
                              <Typography variant="body2" color="text.secondary">
                                Due: {new Date(order.due_date).toLocaleDateString('de-DE')}
                              </Typography>
                            )}
                          </Grid>
                          
                          <Grid item xs={12} md={2}>
                            <Box display="flex" flexDirection="column" gap={1}>
                              <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => handleOrderAction(order.id, 'view_details')}
                              >
                                View Details
                              </Button>
                              {order.status === 'sent' && (
                                <Button 
                                  size="small" 
                                  variant="outlined"
                                  color="info"
                                  onClick={() => handleOrderAction(order.id, 'track_delivery')}
                                >
                                  Track Delivery
                                </Button>
                              )}
                              {order.status === 'delivered' && (
                                <Button 
                                  size="small" 
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleOrderAction(order.id, 'mark_completed')}
                                >
                                  Mark Complete
                                </Button>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Order Process Integration Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Process Integration Summary
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Workflow Integration:</strong> This case follows the Supplier Quote → Order Conversion workflow.
                </Typography>
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Case Workflow (Supplier Quote)
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2">• Quote Request Generated</Typography>
                    <Typography variant="body2">• Supplier Contacted</Typography>
                    <Typography variant="body2">• Quote Received & Selected</Typography>
                    <Typography variant="body2" fontWeight="bold">• Converted to Order ✓</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Order Workflow (Fulfillment)
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2">• Order Created (Draft)</Typography>
                    <Typography variant="body2">• Order Finalized (Ready)</Typography>
                    <Typography variant="body2">• Order Sent to Supplier</Typography>
                    <Typography variant="body2">• Order Delivered</Typography>
                    <Typography variant="body2">• Order Completed</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // Helper functions
  function getOrderStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case 'draft': return 'default';
      case 'ready': return 'primary';
      case 'sent': return 'info';
      case 'delivered': return 'warning';
      case 'completed': return 'success';
      default: return 'default';
    }
  }

  function getOrderProgressPercentage(status: string): number {
    switch (status) {
      case 'draft': return 20;
      case 'ready': return 40;
      case 'sent': return 60;
      case 'delivered': return 80;
      case 'completed': return 100;
      default: return 0;
    }
  }

  // Helper function to get display status based on workflow state
  function getDisplayStatus(status: string, workflowState: string): string {
    // Prioritize workflow state for more accurate status display
    switch (workflowState) {
      case 'completed':
      case 'case_lost':
      case 'case_closed':
        return 'CLOSED';
      case 'case_won':
      case 'waiting_for_file_upload':
      case 'quote_selected':
      case 'converted_to_order':
        return 'RESOLVED';
      case 'preprocessing_needed':
      case 'waiting_for_customer_decision':
      case 'supplier_contacted':
      case 'supplier_response_received':
        return 'IN PROGRESS';
      case 'case_created':
      case 'preprocessing_decision':
      case 'supplier_case_created':
      case 'supplier_quote_generated':
        return 'NEW';
      default:
        return status?.toUpperCase() || 'UNKNOWN';
    }
  }

  // Helper function to get status color
  function getStatusColor(status: string, workflowState: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (workflowState) {
      case 'completed':
      case 'case_won':
      case 'waiting_for_file_upload':
      case 'quote_selected':
      case 'converted_to_order':
        return 'success';
      case 'case_lost':
      case 'case_closed':
        return 'error';
      case 'preprocessing_needed':
      case 'waiting_for_customer_decision':
      case 'supplier_contacted':
      case 'supplier_response_received':
        return 'warning';
      case 'case_created':
      case 'preprocessing_decision':
      case 'supplier_case_created':
      case 'supplier_quote_generated':
        return 'info';
      default:
        return 'default';
    }
  }
};

// Customer Preprocessing Tab Component
const CustomerPreprocessingTab: React.FC<{ 
  selectedCase: CaseData; 
  currentWorkflowState: string;
  onWorkflowTransition: (fromState: string, toState: string) => Promise<void>;
}> = ({ selectedCase, currentWorkflowState, onWorkflowTransition }) => {
  return (
    <CustomerPreprocessingManager
      selectedCase={selectedCase}
      currentWorkflowState={currentWorkflowState}
      onWorkflowTransition={onWorkflowTransition}
      onStageComplete={(stage, data) => {
        console.log(`✅ [PREPROCESSING] Stage ${stage} completed:`, data);
      }}
    />
  );
};

const CustomerApprovalTab: React.FC<{ 
  selectedCase: CaseData; 
  currentWorkflowState: string;
  onWorkflowTransition: (fromState: string, toState: string) => Promise<void>;
}> = ({ selectedCase, currentWorkflowState, onWorkflowTransition }) => (
  <CustomerApprovalManager
    selectedCase={{
      id: selectedCase.id,
      case_number: selectedCase.case_number || selectedCase.id,
      customer_name: selectedCase.customer_name || 'Unknown Customer',
      customer_email: selectedCase.customer_email || '',
      workflow_state: currentWorkflowState,
      metadata: selectedCase.metadata || {}
    }}
    currentWorkflowState={currentWorkflowState}
    onWorkflowTransition={onWorkflowTransition}
    onCaseComplete={() => {
      console.log('Customer case completed');
      // Will be handled by parent component
    }}
  />
);

const FilesTab: React.FC<{ selectedCase: CaseData }> = ({ selectedCase }) => (
  <Box>
    <CustomerCaseFileManager
      caseId={selectedCase.id}
      onFilesChanged={() => {
        console.log('Files changed for case:', selectedCase.id);
      }}
    />
  </Box>
);

const EmailTab: React.FC<{ selectedCase: CaseData }> = ({ selectedCase }) => (
  <Box>
    <EmailIntegrationPanel
      caseId={selectedCase.id}
      caseType={selectedCase.case_type}
    />
  </Box>
);

export default WorkflowCaseDashboard;
